/**
 * useOrgMemberProfile Hook (V5.17)
 *
 * 조직원관리에서 조직원 상세 프로필 카드를 조회하는 훅입니다.
 *
 * **기능:**
 * - getOrgMemberProfileCard callable 함수 호출
 * - 로딩/에러 상태 관리
 * - 조직 스코프 외 접근 시 permission-denied 에러 처리
 *
 * **사용 예시:**
 * ```tsx
 * const { profile, isLoading, error, fetchProfile } = useOrgMemberProfile();
 *
 * // Modal 오픈 시 fetch
 * useEffect(() => {
 *   if (selectedUserId) {
 *     fetchProfile(selectedUserId);
 *   }
 * }, [selectedUserId]);
 * ```
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 프로필 카드 데이터 타입
 */
export interface OrgMemberProfileCardData {
  // 인적사항
  id: string;
  nameKo: string;
  employeeNumber: string;
  email: string;
  birthDate: string | null;

  // 직무
  positionTitle: string;
  jobTitle: string;

  // 고용/계약
  currentEmploymentType: string;
  currentEmploymentTypeCode: string; // V62: DISPATCH 가드용
  status: string;
  firstHireDate: string | null;
  ngleFirstHireDate: string | null;
  probationEndDate: string | null;
  regularHireDate: string | null;

  // 학력/기타
  educationLevel: string;
  educationLevelLabel: string;
  major: string;
  certificatesText: string;
  trainingsText: string;
  commentText: string;

  // 연락처
  phoneNumber: string;
  address: string;

  // V55.13: 프로필 사진
  photoPath: string;
}

/**
 * Hook 반환 타입
 */
export interface UseOrgMemberProfileResult {
  /** 프로필 데이터 */
  profile: OrgMemberProfileCardData | null;
  /** 로딩 상태 */
  isLoading: boolean;
  /** 에러 메시지 */
  error: string | null;
  /** 프로필 조회 함수 */
  fetchProfile: (targetUserId: string) => Promise<void>;
  /** 상태 리셋 함수 */
  reset: () => void;
}

/**
 * 조직원 프로필 카드 조회 훅
 */
export function useOrgMemberProfile(): UseOrgMemberProfileResult {
  const [profile, setProfile] = useState<OrgMemberProfileCardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (targetUserId: string) => {
    if (!functions) {
      setError('Firebase Functions가 초기화되지 않았습니다.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setProfile(null);

    try {
      const getOrgMemberProfileCardFn = httpsCallable<
        { targetUserId: string },
        { success: boolean; profile: OrgMemberProfileCardData | null; message?: string }
      >(functions, 'getOrgMemberProfileCard');

      const result = await getOrgMemberProfileCardFn({ targetUserId });

      if (result.data.success && result.data.profile) {
        setProfile(result.data.profile);
      } else {
        setError(result.data.message || '프로필을 불러올 수 없습니다.');
      }
    } catch (err: any) {
      console.error('[useOrgMemberProfile] 조회 실패:', err);

      // 에러 코드별 사용자 친화 메시지
      if (err.code === 'functions/permission-denied') {
        setError('해당 직원의 정보를 조회할 권한이 없습니다.');
      } else if (err.code === 'functions/not-found') {
        setError('해당 직원을 찾을 수 없습니다.');
      } else if (err.code === 'functions/unauthenticated') {
        setError('로그인이 필요합니다.');
      } else {
        setError(err.message || '프로필 조회 중 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setProfile(null);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    profile,
    isLoading,
    error,
    fetchProfile,
    reset,
  };
}
